import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useEnrollUserInCourseMutation, useMakeCoursePaymentMutation, Course } from "@axio/graphql/generated/graphql";
import { Animated, Platform } from "react-native";
import handleOpenLink from "../utils/handleOpenLink";

type CourseDataWithPreReqs = Partial<Course & { preRequisitesCompleted: boolean }>;

type CourseDetailsContextType = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  courseData: CourseDataWithPreReqs;
  setCourseData: (courseData: CourseDataWithPreReqs) => void;
  onEnroll: (id: string, hasCost: boolean, callback?: () => void) => Promise<void>;
  loading: boolean;
  slideAnim: Animated.Value;
};

const defaultCourseData: CourseDataWithPreReqs = {
  courseId: "",
  name: "",
  description: "",
  objectives: [],
  targetAudience: "",
  modules: [],
  cost: 0,
  preRequisitesCompleted: false,
  preRequisites: [],
};

const CourseDetailsContext = createContext<CourseDetailsContextType>({
  isVisible: false,
  setIsVisible: () => {},
  courseData: defaultCourseData,
  setCourseData: () => {},
  onEnroll: () => Promise.resolve(),
  loading: false,
  slideAnim: new Animated.Value(0),
});

const CourseDetailsProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [courseData, setCourseData] = useState(defaultCourseData);

  const courseDetailsSlideAnim = useRef(new Animated.Value(270)).current;

  useEffect(() => {
    if (isVisible) {
      Animated.timing(courseDetailsSlideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(courseDetailsSlideAnim, {
        toValue: 270,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, courseDetailsSlideAnim]);

  const [createCheckoutSession, { loading: creatingPaymentSession }] = useMakeCoursePaymentMutation();
  const [enrollStudent, { loading: enrollingUserInCourse }] = useEnrollUserInCourseMutation({
    refetchQueries: ["GetEnrolledCoursesForStudent", "GetAvailableCoursesForStudent"],
    awaitRefetchQueries: true,
  });

  const onEnroll = async (id, hasCost, callback) => {
    try {
      if (hasCost) {
        const response = await createCheckoutSession({
          variables: {
            courseId: id,
          },
        });

        const stripeUrl = response?.data?.createCheckoutSession?.url;

        if (!stripeUrl) {
          console.error("No Stripe URL returned from checkout session");
          throw new Error("Payment processing failed - no checkout URL");
        }

        if (Platform.OS === "web") {
          window.open(stripeUrl, "_blank");
        } else {
          await handleOpenLink(stripeUrl);
        }
      } else {
        await enrollStudent({
          variables: {
            courseId: id,
          },
        });
      }

      setIsVisible(false);
      setCourseData(defaultCourseData);

      if (typeof callback === "function") {
        callback();
      }
    } catch (error) {
      console.error("Enrollment error:", error);
    }
  };

  const loading = creatingPaymentSession || enrollingUserInCourse;

  return (
    <CourseDetailsContext.Provider
      value={{
        isVisible,
        setIsVisible,
        courseData,
        setCourseData,
        onEnroll,
        loading,
        slideAnim: courseDetailsSlideAnim,
      }}>
      {children}
    </CourseDetailsContext.Provider>
  );
};

const useCourseDetails = () => {
  const context = useContext(CourseDetailsContext);
  if (!context) {
    throw new Error("useCourseDetails must be used within a CourseDetailsProvider");
  }
  return context;
};

export { CourseDetailsProvider, useCourseDetails };
